import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Space, Typography, Button } from 'antd';
import { ExpandOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { VIP_STATUS_LABELS, VIP_STATUS_COLORS, VipStatus } from '../types';
import apiClient from '../services/api';

const { Title } = Typography;

// Interface for New user data
interface NewUser {
  account_id: string;
  full_name: string;
  email: string;
  specialty: string;
  prefecture: string;
  clinic_name: string;
  vip_status: number;
  created_at: string;
  total_usage_seconds: number;
}

interface NewUsersResponse {
  new_users: NewUser[];
  date_range: string;
  total_count: number;
}

interface NewUsersListProps {
  selectedDate: string;
}

const NewUsersList: React.FC<NewUsersListProps> = ({ selectedDate }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<NewUser[]>([]);
  const [dateRange, setDateRange] = useState<string>('');

  useEffect(() => {
    if (selectedDate) {
      fetchNewUsers(selectedDate);
    }
  }, [selectedDate]);

  const fetchNewUsers = async (date: string) => {
    try {
      setLoading(true);
      const response: NewUsersResponse = await apiClient.get(`/api/analytics/new-users/${date}`);
      console.log('New users response:', response);
      setData(response.new_users);
      setDateRange(response.date_range);
    } catch (error) {
      console.error('Failed to fetch new users:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<NewUser> = [
    {
      title: 'ユーザー名',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 150,
      ellipsis: true,
      render: (name: string, record: NewUser) => (
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            marginBottom: '2px',
            flexWrap: 'wrap'
          }}>
            <span style={{ 
              fontWeight: 'bold',
              fontSize: '12px',
              color: name === '退会済み' ? 'red' : 'inherit'
            }}>
              {name || `ユーザー${record.account_id.slice(-8)}`}
            </span>
            <Tag 
              color={VIP_STATUS_COLORS[record.vip_status as VipStatus]}
              style={{ fontSize: '9px', padding: '0 4px', lineHeight: '16px' }}
            >
              {VIP_STATUS_LABELS[record.vip_status as VipStatus]}
            </Tag>
          </div>
          <div style={{ fontSize: '10px', color: '#666', wordBreak: 'break-all' }}>
            {record.email}
          </div>
        </div>
      ),
    },
    {
      title: 'クリニック',
      dataIndex: 'clinic_name',
      key: 'clinic_name',
      width: 120,
      ellipsis: true,
      render: (clinic: string, record: NewUser) => (
        <span 
          style={{ 
            color: '#1890ff', 
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '12px'
          }}
          onClick={() => {
            if (clinic && clinic !== 'なし') {
              const searchQuery = record.prefecture ? 
                `${clinic} ${record.prefecture}` : 
                clinic;
              const googleSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
              window.open(googleSearchUrl, '_blank');
            }
          }}
        >
          {clinic || 'なし'}
        </span>
      ),
    },
    {
      title: '都道府県',
      dataIndex: 'prefecture',
      key: 'prefecture',
      width: 70,
      render: (text: string) => <span style={{ fontSize: '12px' }}>{text}</span>,
    },
    {
      title: '専業',
      dataIndex: 'specialty',
      key: 'specialty',
      width: 80,
      ellipsis: true,
      render: (text: string) => <span style={{ fontSize: '12px' }}>{text}</span>,
    },
    {
      title: '利用時間',
      dataIndex: 'total_usage_seconds',
      key: 'total_usage_seconds',
      width: 80,
      align: 'center',
      render: (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        if (minutes === 0) {
          return <span style={{ color: '#999', fontSize: '11px' }}>未使用</span>;
        }
        return <span style={{ fontSize: '11px' }}>{minutes}分</span>;
      },
    },
  ];

  return (
    <Card 
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Title level={4} style={{ margin: 0 }}>新規ユーザー一覧</Title>
            <span style={{ color: '#999', fontSize: '14px' }}>New Users List</span>
            {selectedDate && (
              <span style={{ color: '#1890ff', fontSize: '12px' }}>({selectedDate})</span>
            )}
          </Space>
          <Button
            type="text"
            icon={<ExpandOutlined />}
            onClick={() => window.open(`/fullscreen/new-users?date=${selectedDate}`, '_blank')}
            style={{ marginLeft: 'auto' }}
          />
        </div>
      }
      style={{ height: '100%', minHeight: '400px' }}
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Table
          loading={loading}
          columns={columns}
          dataSource={data}
          rowKey="account_id"
          pagination={{
            pageSize: 5,
            showSizeChanger: false,
            showTotal: (total) => `全 ${total} 件`,
            size: 'small',
            style: { marginBottom: 0, marginTop: '8px' }
          }}
          scroll={{ x: 'max-content' }}
          size="small"
          style={{ fontSize: '12px' }}
        />
      </div>
    </Card>
  );
};

export default NewUsersList;